import { ReferralStatus } from "@/lib/schema";
import { updateReferralStatus, onCandidateHired } from "@/api/referrals";

export interface StatusChangeOptions {
  referralId: string;
  newStatus: ReferralStatus;
  userId: string;
  notes?: string;
  rejectionNotes?: string; // Feedback notes for rejected referrals
  onSuccess?: (referralId: string, newStatus: ReferralStatus) => void;
  onError?: (error: string, referralId: string, newStatus: ReferralStatus) => void;
}

export interface HireStatusChangeOptions {
  referralId: string;
  userId: string;
  newStatus: ReferralStatus;
  hiredDate: string;
  agreedSalary: string;
  totalRewardAmount?: number;
  baseRewardAmount?: number;
  bonusAmount?: number;
  hasBonus?: boolean;
  onSuccess?: (referralId: string, newStatus: ReferralStatus) => void;
  onError?: (error: string, referralId: string, newStatus: ReferralStatus) => void;
}

/**
 * Utility function to handle referral status changes
 * Handles the API call and provides consistent error handling
 */
export async function changeReferralStatus({
  referralId,
  newStatus,
  userId,
  notes,
  rejectionNotes,
  onSuccess,
  onError
}: StatusChangeOptions): Promise<boolean> {
  try {
    const changeNotes = notes || `Status changed to ${newStatus}`;
    
    await updateReferralStatus(referralId, newStatus, userId, changeNotes, rejectionNotes);
    
    // Call success callback if provided
    onSuccess?.(referralId, newStatus);
    
    return true;
  } catch (error) {
    console.error(`Error updating referral ${referralId} status to ${newStatus}:`, error);
    
    const errorMessage = `Failed to update referral status to ${newStatus.replace('_', ' ').toUpperCase()}. Please try again.`;
    
    // Call error callback if provided
    onError?.(errorMessage, referralId, newStatus);
    
    return false;
  }
}

/**
 * Utility function to handle HIRED status changes with additional data
 * This calls a Firebase function that handles:
 * 1. Updating referral status to HIRED
 * 2. Rejecting all other referrals for the same job  
 * 3. Creating rewards with bonus calculations
 * 4. Recording hire details (date, salary, bonus info)
 */
export async function changeReferralStatusToHired({
  referralId,
  userId,
  hiredDate,
  agreedSalary,
  totalRewardAmount,
  baseRewardAmount,
  bonusAmount,
  hasBonus,
  onSuccess,
  onError
}: HireStatusChangeOptions): Promise<boolean> {
  try {
    // Call the Firebase function to process the hire
    const result = await onCandidateHired({
      referralId,
      hiredDate,
      agreedSalary
    });
    
    // Call success callback if provided
    onSuccess?.(referralId, ReferralStatus.HIRED);
    
    return true;
  } catch (error: any) {
    const errorMessage = error.message || "Failed to process the hire. Please try again.";
    
    // Call error callback if provided
    onError?.(errorMessage, referralId, ReferralStatus.HIRED);
    
    return false;
  }
}

/**
 * Get display name for referral status
 */
export function getStatusDisplayName(status: ReferralStatus): string {
  return status.replace('_', ' ').toUpperCase();
}
